import SubframeCore, { IconName } from "@subframe/core";
import { Recipient } from "../redux/interfaces/reducersInterfaces";
import { Message } from "../Interfaces/messagesInterface";
import moment from "moment";
import { getCountryCodeFromNumber } from "../Utils/countryNameToCode";
import { formatPhoneNumber } from "../Utils/formatPhoneNumber";

interface RecipientProps {
  recipient: Recipient;
  lastMessage: Message;
}

function Friend({ recipient, lastMessage }: RecipientProps) {
  const countryCode = recipient && getCountryCodeFromNumber(recipient?.phone_number.toString());
  
  // Safety check - if recipient is invalid, don't render
  if (!recipient || !recipient.id || !recipient.phone_number) {
    return null;
  }
  
  // Safety check - ensure lastMessage is valid
  if (!lastMessage || typeof lastMessage !== 'object') {
    return null;
  }
  
  return (
    <>
      <div className="h-12 w-12 flex-none rounded-full bg-gradient-to-br from-blue-100 to-indigo-200 flex items-center justify-center shadow-sm border-2 border-white">
        <span className="text-lg font-semibold text-blue-700">
          {recipient.name ? recipient.name.charAt(0).toUpperCase() : 
           recipient.phone_number ? recipient.phone_number.toString().charAt(0) : "?"}
        </span>
      </div>
      <div className="flex grow shrink-0 basis-0 flex-col items-start cursor-pointer select-none">
        <div className="flex w-full items-center gap-2">
          <span
            className={`text-body-bold font-body-bold text-default-font ${
              lastMessage &&
              lastMessage.recipient &&
              lastMessage.status === "unread"
                ? "font-extrabold"
                : ""
            }`}
          >
            {recipient.name
              ? recipient.name
              : recipient.phone_number
              ? formatPhoneNumber(
                  recipient.phone_number,
                  countryCode?.countryCode ? countryCode.countryCode : "US"
                )
              : "No name available"}
          </span>
          <div className="flex grow shrink-0 basis-0 items-center justify-end gap-2 self-stretch">
            <span
              className={`text-caption font-caption text-default-font ${
                lastMessage &&
                lastMessage.recipient &&
                lastMessage.status === "unread"
                  ? "font-bold text-success-500"
                  : ""
              }`}
            >
              {lastMessage?.created_at
                ? moment(lastMessage.created_at).isSame(moment(), "day")
                  ? moment(lastMessage.created_at).local().format("hh:mm A")
                  : moment(lastMessage.created_at).local().format("D/MM/YYYY")
                : ""}
            </span>
            {lastMessage &&
              lastMessage.recipient &&
              lastMessage.status === "unread" && (
                <SubframeCore.Icon
                  className="text-caption-bold font-caption-bold text-success-500"
                  name="FeatherMail"
                />
              )}
          </div>
        </div>
        <div className="flex w-full flex-col items-start gap-1">
          <SubframeCore.Icon
            className="hidden text-body-bold font-body-bold text-subtext-color"
            name="FeatherCornerUpLeft"
          />
          <div className="flex w-full items-center justify-end gap-1">
            <SubframeCore.Icon
              className={`text-body font-body ${
                lastMessage?.status === "read"
                  ? "text-brand-500"
                  : "text-subtext-color"
              } ${lastMessage?.recipient && "hidden"}`}
              name={
                (lastMessage?.status === "delivered" ||
                lastMessage?.status === "read"
                  ? "FeatherCheckCheck"
                  : "FeatherCheck") as IconName
              }
            />
            <span
              className={`line-clamp-1 grow shrink-0 basis-0 text-body font-body text-default-font`}
            >
              {lastMessage &&
                lastMessage.system_user &&
                typeof lastMessage.system_user === 'string' &&
                lastMessage.system_user}
              <span
                className={`${
                  lastMessage && lastMessage.status === "unread"
                    ? "font-bold"
                    : ""
                }`}
              >
                {lastMessage && lastMessage.recipient && typeof lastMessage.recipient === 'string' && lastMessage.recipient}
              </span>
            </span>
          </div>
          <div className="flex flex-wrap items-start gap-1">
            {Array.isArray(recipient.global_label) &&
              recipient.global_label.map((label: string, index: number) => (
                <span
                  key={`ai-${index}`}
                  className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800 border border-amber-200"
                >
                  {label}
                </span>
              ))}

            {Array.isArray(recipient.manual_label) &&
              recipient.manual_label.map((label: string, index: number) => (
                <span
                  key={`manual-${index}`}
                  className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800 border border-emerald-200"
                >
                  {label}
                </span>
              ))}
          </div>
        </div>
      </div>
    </>
  );
}

export default Friend;
